/**
 * Shared company-picker helpers for Users (AddUser) and Brands (AddBrandUser).
 * UI stays on SingleSelect / CompanyHierarchySingleSelect — this file is data only.
 */

export const ADD_TO_NEW_COMPANY = {
  id: "__add_to_new_company__",
  value: "__add_to_new_company__",
  label: "Add to new company",
};

export const isAddToNewCompanyOption = (option) =>
  option != null && String(option.id) === ADD_TO_NEW_COMPANY.id;

export const toCompanyOption = (companyDetail) => {
  const label =
    typeof companyDetail.label !== "undefined" ? companyDetail.label : companyDetail.name;
  const name =
    typeof companyDetail.name !== "undefined" ? companyDetail.name : companyDetail.label;

  return {
    id: companyDetail.id,
    value: companyDetail.id,
    label,
    name,
    // Brand edit uses these to pre-tick campaigns for an already-assigned company
    campaign_ids: Array.isArray(companyDetail.campaign_ids) ? companyDetail.campaign_ids : [],
  };
};

const companyIdSet = (ids) => {
  const set = {};
  ids.forEach((id) => {
    set[String(id)] = true;
  });
  return set;
};

export const ALREADY_ADDED_COMPANY_TOOLTIP = "Already added";

/**
 * Keep the full hierarchy. Companies this user already belongs to stay in the
 * list so the tree is still easy to read, with " (added)" on the name.
 * Those rows are greyed out and cannot be selected again.
 */
export const markAlreadyAddedCompanies = (nodes, assignedIds) => {
  const assignedIdsByKey = companyIdSet(assignedIds);

  const markNode = (node) => {
    const childCompanies = Array.isArray(node.children) ? node.children.map(markNode) : [];
    const companyIsAlreadyAdded = assignedIdsByKey[String(node.id)] === true;
    const companyName =
      typeof node.label === "string" && node.label !== "" ? node.label : node.name;

    return {
      ...node,
      children: childCompanies,
      alreadyAdded: companyIsAlreadyAdded,
      label: companyIsAlreadyAdded === true ? companyName + " (added)" : companyName,
    };
  };

  if (Array.isArray(nodes) !== true) {
    return [];
  }

  return nodes.map(markNode);
};

export const isAlreadyAddedCompany = (company) =>
  company != null && company.alreadyAdded === true;

/**
 * Drop companies the user already has from a hierarchy tree.
 * Assigned parents stay as grouping rows (selectable: false) when they still have children.
 */
export const excludeAssignedFromTree = (nodes, assignedIds) => {
  const assigned = companyIdSet(assignedIds);

  const walk = (node) => {
    const children = (Array.isArray(node.children) ? node.children : [])
      .map(walk)
      .filter((child) => child !== null);
    const isAssigned = assigned[String(node.id)] === true;

    if (isAssigned === true && children.length === 0) {
      return null;
    }
    if (isAssigned === true) {
      return {
        ...node,
        children,
        selectable: false,
      };
    }
    return {
      ...node,
      children,
    };
  };

  return (Array.isArray(nodes) ? nodes : []).map(walk).filter((node) => node !== null);
};

/**
 * Brand add/edit only. Drop companies with nothing shareable, but keep the
 * ancestor path so a shareable store still sits under its branch / national / root.
 *
 * - true → keep (enabled)
 * - false with a kept descendant → keep as a disabled header
 * - false with no kept descendants → drop the whole subtree
 * - missing flag → keep (same as today's enable behaviour)
 *
 * Do not use this on Users-tab or campaign owner trees.
 */
export const keepShareableHierarchy = (nodes) => {
  const walk = (node) => {
    const sourceChildren = Array.isArray(node.children) ? node.children : [];
    const children = sourceChildren.map(walk).filter((child) => child !== null);
    // A shareable parent can be left with no children. Keep it bold in the dropdown.
    const isHierarchyParent = sourceChildren.length > 0;

    const kept = (keptChildren) => ({
      ...node,
      children: keptChildren,
      ...(isHierarchyParent === true ? { isHierarchyParent: true } : {}),
    });

    const hasShareable = node.has_shareable_campaign === true;
    const hasNone = node.has_shareable_campaign === false;

    if (hasShareable === true) {
      return kept(children);
    }
    // A sibling store with something to share keeps this header even when it is false.
    if (children.length > 0) {
      return kept(children);
    }
    if (hasNone === true) {
      return null;
    }
    return kept(children);
  };

  return (Array.isArray(nodes) ? nodes : []).map(walk).filter((node) => node !== null);
};

/**
 * Clip add/clone owner only. Same rules as keepShareableHierarchy but for has_active_campaign.
 * Do not use on brand share, Users-tab, or campaign owner trees.
 */
export const keepActiveCampaignHierarchy = (nodes) => {
  const walk = (node) => {
    const sourceChildren = Array.isArray(node.children) ? node.children : [];
    const children = sourceChildren.map(walk).filter((child) => child !== null);
    const isHierarchyParent = sourceChildren.length > 0;

    const kept = (keptChildren) => ({
      ...node,
      children: keptChildren,
      ...(isHierarchyParent === true ? { isHierarchyParent: true } : {}),
    });

    const hasActive = node.has_active_campaign === true;
    const hasNone = node.has_active_campaign === false;

    if (hasActive === true) {
      return kept(children);
    }
    if (children.length > 0) {
      return kept(children);
    }
    if (hasNone === true) {
      return null;
    }
    return kept(children);
  };

  return (Array.isArray(nodes) ? nodes : []).map(walk).filter((node) => node !== null);
};

/**
 * Hierarchy API wraps nationals under the tenant root.
 * Users-tab pickers skip that root (National → Branch → Store only).
 * Brand create keeps the full tree — do not use this there.
 */
export const nationalCompaniesOnly = (retailers) => {
  const tree = Array.isArray(retailers) ? retailers : [];
  if (tree.length === 1 && tree[0].type === "root") {
    return Array.isArray(tree[0].children) ? tree[0].children : [];
  }
  return tree;
};
